import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import * as Sentry from "@sentry/react";
import AppNavigationContext from "@minohealth/common/services/AppNavigationContext";
import { PortalManager } from "@minohealth/common/utils/popups";
import { LinkedInCallback } from "react-linkedin-login-oauth2";
import ChatLayout from "./components/layout/chat-layout";
import useActivityTracker from "@minohealth/common/hooks/useActivityTracker";
import mixpanel from "mixpanel-browser";
import { SSEProvider } from "./utils/SSEProvider";
import { ToastContainer } from 'react-toastify';
import { Provider } from "jotai";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";
import { store } from "@/lib/store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { AlertTriangle } from "lucide-react";

mixpanel.init(import.meta.env.VITE_MIXPANEL_TRACKING_TOKEN);

function redactSensitiveFields(data, sensitiveFields) {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (sensitiveFields.includes(key)) return [key, "[REDACTED]"];
      return [key, value];
    })
  );
}

function redactPossibleSensitiveFields(data, sensitiveFields) {
  if (typeof data === "object" && data != null) {
    return redactSensitiveFields(data, sensitiveFields);
  }
  return data;
}

function SentryUserContext() {
  const user = useSelector((state) => state.app.datastate.user);
  useEffect(() => {
    if (user?._id) {
      Sentry.setUser({
        id: user._id,
        email: user.email,
        username: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email,
        role: user.role,
        plan: user.currentplan?.name,
        workerType: user.workerType,
        facilityName: user.facilityName,
        country: user.country,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user?._id]);
  return null;
}


const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error, query) {
      Sentry.captureException(error, {
        tags: { type: "query", ...query.meta?.sentry?.tags },
        contexts: {
          Query: { queryKey: query.queryKey, ...query.meta?.sentry?.context },
          ...query.meta?.sentry?.contexts,
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError(error, variables, context, mutation) {
      const sensitiveFields = mutation.meta?.sentry?.sensitiveFields ?? [];
      Sentry.captureException(error, {
        tags: { type: "mutation", ...mutation.meta?.sentry?.tags },
        contexts: {
          Mutation: {
            mutationKey: mutation.options.mutationKey,
            variables: redactPossibleSensitiveFields(variables, sensitiveFields),
            context: redactPossibleSensitiveFields(context, sensitiveFields),
            ...mutation.meta?.sentry?.context,
          },
          ...mutation.meta?.sentry?.contexts,
        },
      });
    },
  }),
})

export default function App() {
  const [appNavHistory, setAppNavHistory] = useState({});
  const appNavContext = useMemo(() => ({ appNavHistory, setAppNavHistory }), [appNavHistory]);

  const { showWarning, countdown, handleLogout, handleCancelLogout } = useActivityTracker(
    "/login",
    30,  // 1 minute timeout for testing
    30  // 30 seconds warning
  );



  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="moremi-theme">
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <SSEProvider>
            <SentryUserContext />
            <AppNavigationContext.Provider value={appNavContext}>
              <PortalManager />
              <BrowserRouter>
                <Switch>
                  <Route exact path="/linkedin" component={LinkedInCallback} />
                  <Route path="/" render={(props) => <ChatLayout {...props} />} />
                </Switch>
              </BrowserRouter>

              {/* Inactivity Warning Dialog */}
              <Dialog open={showWarning} onOpenChange={() => { }}>
                <DialogContent
                  className="sm:max-w-md [&>button]:hidden"
                  onPointerDownOutside={(e) => e.preventDefault()}
                  onEscapeKeyDown={(e) => e.preventDefault()}
                >
                  <DialogHeader>
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-6 w-6 text-yellow-500" />
                      <DialogTitle className="text-xl">Inactivity Warning</DialogTitle>
                    </div>
                  </DialogHeader>
                  <DialogDescription className="text-base py-4">
                    You have been inactive for a while. For your security, you will be automatically
                    logged out in <span className="font-bold text-lg text-red-500">{countdown}</span> seconds.
                  </DialogDescription>
                  <DialogFooter className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      onClick={handleLogout}
                      className="w-full sm:w-auto"
                    >
                      Logout Now
                    </Button>
                    <Button
                      onClick={handleCancelLogout}
                      className="w-full sm:w-auto bg-primary"
                    >
                      Stay Logged In
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

            </AppNavigationContext.Provider>
          </SSEProvider>

          <Toaster
            position="top-right"
            closeButton
          />

          <ReactQueryDevtools initialIsOpen={false} />
        </Provider>
        <ToastContainer />
      </QueryClientProvider>
    </ThemeProvider>
  )
}